import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  RemoteWorkImageAndButtonSliceDefault,
  RemoteWorkImageAndButtonSliceOneImage,
} from '../../../prismicio-types';

enum SLICE_VARIATION {
  default = 'default',
  oneImage = 'oneImage',
}

/**
 * Props for `RemoteWorkImageAndButton`.
 */
export type RemoteWorkImageAndButtonProps =
  SliceComponentProps<Content.RemoteWorkImageAndButtonSlice>;

/**
 * Component for "RemoteWorkImageAndButton" Slices.
 */
const RemoteWorkImageAndButton = ({
  slice,
}: RemoteWorkImageAndButtonProps): JSX.Element => {
  const {
    primary: { title, description, button_label, button_link, icon },
  } = slice;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="md:py-28 md:px-16 px-5 py-16 text-white max-w-screen-2xl mx-auto"
    >
      <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
        <div>
          {icon && <PrismicNextImage field={icon} className="w-[32px] mb-2" />}
          <PrismicRichText field={title} />
        </div>
        <div
          className={`max-w-[540px] ${slice.variation !== SLICE_VARIATION.oneImage ? 'md:pl-20' : ''}`}
        >
          <PrismicRichText field={description} />
          {button_label && (
            <div className="mt-10">
              <PrismicNextLink field={button_link} className="button-bordered">
                <span>{button_label}</span>
              </PrismicNextLink>
            </div>
          )}
        </div>
      </div>

      {slice.variation === SLICE_VARIATION.default && (
        <DefaultVariation slice={slice} />
      )}

      {slice.variation === SLICE_VARIATION.oneImage && (
        <OneImageVariation slice={slice} />
      )}
    </section>
  );
};

const DefaultVariation = ({
  slice,
}: {
  slice: RemoteWorkImageAndButtonSliceDefault;
}) => {
  const image_group = slice.primary.image_group;
  return (
    <div className="grid grid-cols-2 gap-4 mt-16 md:pr-16 md:min-h-[400px]">
      <div className="col-span-1 mb-10 overflow-hidden rounded-2xl">
        <div>
          <PrismicNextImage
            field={image_group[0]!.image}
            className="w-full h-full object-contain"
          />
        </div>
      </div>
      <div className="col-span-1 flex flex-col justify-between md:ml-10 w-full">
        <div className="flex-1 relative overflow-hidden rounded-2xl">
          <PrismicNextImage
            field={image_group[1]!.image}
            className="object-cover object-top absolute md:top-[-40px] w-full h-[calc(100%+40px)]"
          />
        </div>
        <div className="flex-1">
          <div className="relative top-[20px] md:top-[60px] overflow-hidden rounded-2xl h-[80px] md:h-3/4 md:w-2/4 lg:1/4 ">
            <div className="h-full">
              <PrismicNextImage
                field={image_group[2]!.image}
                className="object-cover object-center w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OneImageVariation = ({
  slice,
}: {
  slice: RemoteWorkImageAndButtonSliceOneImage;
}) => {
  return (
    <div className="mt-20 mx-auto relative overflow-hidden">
      <div className="w-full h-full">
        <PrismicNextImage
          field={slice.primary.image}
          className="object-cover w-full h-full"
        />
      </div>
    </div>
  );
};

export default RemoteWorkImageAndButton;
